import React, { useState } from 'react';
import './App.css';
import LandingPage from './components/LandingPage';
import FileUpload from './components/FileUpload';
import ColumnMapping from './components/ColumnMapping';
import HyperparameterSelection from './components/HyperparameterSelection';
import ModelTraining from './components/ModelTraining';
import DiagnosticPlots from './components/DiagnosticPlots';
import Results from './components/Results';
import NetworkBackground from './components/NetworkBackground';

function App() {
  const [showLanding, setShowLanding] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [maxStepReached, setMaxStepReached] = useState(1);
  const [sessionData, setSessionData] = useState({
    sessionId: null,
    filename: null,
    userColumns: [],
    availableColumns: {},
    suggestedMappings: [],
    selectedColumns: [],
    columnMappings: [],
    manualMappings: {},
    hyperparameters: null,
    trainingResults: null
  });

  const steps = [
    { number: 1, label: 'Upload CSV' },
    { number: 2, label: 'Select Columns' },
    { number: 3, label: 'Configure Model' },
    { number: 4, label: 'Train Model' },
    { number: 5, label: 'Diagnostic Plots' },
    { number: 6, label: 'View Results' }
  ];

  const handleUploadComplete = (uploadData) => {
    // Check if this is a new file or re-upload
    const isNewFile = sessionData.sessionId !== uploadData.session_id;
    
    setSessionData(prev => ({
      ...prev,
      sessionId: uploadData.session_id,
      filename: uploadData.filename,
      userColumns: uploadData.user_columns,
      availableColumns: uploadData.available_columns,
      suggestedMappings: uploadData.suggested_mappings,
      // Only reset mappings if new file
      selectedColumns: isNewFile ? [] : prev.selectedColumns,
      columnMappings: isNewFile ? [] : prev.columnMappings,
      manualMappings: isNewFile ? {} : prev.manualMappings
    }));
    
    // Reset progress only if new file
    if (isNewFile) {
      setMaxStepReached(2);
    }
    setCurrentStep(2);
  };

  const handleColumnsSelected = (selectedColumns, columnMappings, manualMappings) => {
    setSessionData(prev => ({
      ...prev,
      selectedColumns,
      columnMappings,
      manualMappings,
      // Invalidate training results when columns change
      trainingResults: null,
      classificationThreshold: null
    }));
    setCurrentStep(3);
    // Cap max step at 4 (before training) when columns change
    setMaxStepReached(prev => prev > 4 ? 4 : Math.max(prev, 3));
  };

  const handleHyperparametersSelected = (hyperparameters) => {
    setSessionData(prev => ({
      ...prev,
      hyperparameters,
      // Invalidate training results when hyperparameters change
      trainingResults: null,
      classificationThreshold: null
    }));
    setCurrentStep(4);
    // Cap max step at 4 (before training) when hyperparameters change
    setMaxStepReached(prev => prev > 4 ? 4 : Math.max(prev, 4));
  };

  const handleTrainingComplete = (trainingResults) => {
    setSessionData(prev => ({
      ...prev,
      trainingResults
    }));
    setCurrentStep(5);
    setMaxStepReached(prev => Math.max(prev, 5));
  };

  const resetApp = () => {
    setCurrentStep(1);
    setMaxStepReached(1);
    setSessionData({
      sessionId: null,
      filename: null,
      userColumns: [],
      availableColumns: {},
      suggestedMappings: [],
      selectedColumns: [],
      columnMappings: [],
      manualMappings: {},
      hyperparameters: null,
      trainingResults: null
    });
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepNumber) => {
    // Allow going to any step up to max reached
    if (stepNumber <= maxStepReached) {
      setCurrentStep(stepNumber);
    }
  };

  return (
    <div className="App">
      <NetworkBackground />
      {showLanding ? (
        <LandingPage onStart={() => setShowLanding(false)} />
      ) : (
        <>
          <div className="header">
            <h1 onClick={() => setShowLanding(true)} style={{ cursor: 'pointer' }}>
              🌌 Backsplash Galaxy Classifier
            </h1>
          </div>

          <div className="container">
            <div className="card">
              <div className="progress-steps">
                {steps.map(step => (
                  <div 
                    key={step.number} 
                    className={`step ${currentStep === step.number ? 'active' : ''} ${step.number < maxStepReached ? 'completed' : ''} ${step.number <= maxStepReached ? 'clickable' : ''}`}
                    onClick={() => step.number <= maxStepReached && goToStep(step.number)}
                  >
                    <div className="step-number">{step.number}</div>
                    <div className="step-label">{step.label}</div>
                  </div>
                ))}
              </div>

              {currentStep === 1 && (
                <FileUpload 
                  onUploadComplete={handleUploadComplete} 
                  sessionId={sessionData?.sessionId}
                />
              )}

              {currentStep === 2 && (
                <ColumnMapping
                  availableColumns={sessionData.availableColumns}
                  userColumns={sessionData.userColumns}
                  suggestedMappings={sessionData.suggestedMappings}
                  savedSelectedColumns={sessionData.selectedColumns}
                  savedManualMappings={sessionData.manualMappings}
                  onColumnsSelected={handleColumnsSelected}
                  onBack={goBack}
                />
              )}

              {currentStep === 3 && (
                <HyperparameterSelection
                  onHyperparametersSelected={handleHyperparametersSelected}
                />
              )}

              {currentStep === 4 && (
                <ModelTraining
                  sessionId={sessionData.sessionId}
                  selectedColumns={sessionData.selectedColumns}
                  columnMappings={sessionData.columnMappings}
                  hyperparameters={sessionData.hyperparameters}
                  onTrainingComplete={handleTrainingComplete}
                  onBack={goBack}
                />
              )}

              {currentStep === 5 && (
                <DiagnosticPlots
                  sessionId={sessionData.sessionId}
                  onComplete={(data) => {
                    // Store threshold from diagnostic plots
                    setSessionData(prev => ({
                      ...prev,
                      classificationThreshold: data?.threshold
                    }));
                    setCurrentStep(6);
                    setMaxStepReached(prev => Math.max(prev, 6));
                  }}
                  onBack={goBack}
                />
              )}

              {currentStep === 6 && (
                <Results
                  sessionId={sessionData.sessionId}
                  trainingResults={sessionData.trainingResults}
                  threshold={sessionData.classificationThreshold}
                  onReset={resetApp}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default App;
